/**
 * PanelRow — label/value row for the Property Panel design system.
 *
 * Renders the body of a PanelCard. Encodes the design rule that empty data
 * should explain itself — never an ambiguous em-dash. A row resolves to one
 * of four presentations:
 *
 *   - editable : an always-on inline input (react-final-form), so values can
 *                be changed without an edit button. Active when `editable`
 *                + `name` + `editor` are supplied.
 *   - filled   : a static value (read-only display, or computed result).
 *   - blocked  : a computed field whose dependencies aren't met yet — shows
 *                `blockedReason` in italic instead of a value.
 *   - locked   : a value that exists but can't be edited (lock icon).
 *   - empty    : an editable-but-unset field with a "+ Add …" affordance
 *                (use the explicit `state="empty"` + `onAdd` props).
 *
 * Distinct from the older `DetailRow` — PanelRow is the design-system row and
 * is meant to be reused beyond the property sidebar.
 */
import {
  Add as AddIcon,
  Functions as SigmaIcon,
  InfoOutlined as InfoIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import { Box, Tooltip } from "@mui/material";
import { isArray, isNil, size } from "lodash";
import { useMemo } from "react";
import { useField } from "react-final-form";
import RffBooleanCheckbox from "../../finalForm/inputs/RffBooleanCheckbox";
import RffDateField from "../../finalForm/inputs/RffDateField";
import RffDollarField from "../../finalForm/inputs/RffDollarField";
import RffNumberField from "../../finalForm/inputs/RffNumberField";
import RffCountStepperField from "../../finalForm/inputs/RffCountStepperField";
import RffPercentField from "../../finalForm/inputs/RffPercentField";
import RffSelectField from "../../finalForm/inputs/RffSelectField";
import RffSelectMultipleCheckboxField from "../../finalForm/inputs/RffSelectMultipleCheckboxField";
import RffTextField from "../../finalForm/inputs/RffTextField";
import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";
import { PANEL_TOKENS as T } from "../../primitives/panel/panelTokens";
import { fieldPassesSearch } from "./sidebarFieldUtils";
import RffPhoneField from "../../finalForm/inputs/RffPhoneField";
import RffSsnField from "../../finalForm/inputs/RffSsnField";

const FONT_MONO = "ui-monospace, Menlo, monospace";

// Row type scale. Deliberately a notch under the token's `base` (13) — these
// rows sit two or three to a screen-width in the sidebars' card columns, so
// the denser size keeps long labels on one or two lines instead of truncating.
const ROW_FONT = 11;

// Horizontal inset for the value column's controls. Lives on the control
// itself (an input's own padding, a checkbox's) rather than on the cell that
// wraps it, and only on the left — right-side padding just pushed the caret
// away from the cell edge for no gain.
const CONTROL_INSET = "6px";

// Editable value cells keep the persistent light-blue treatment from the
// previous DetailRow implementation — always visible (not hover-only) so
// editable fields stay obvious at a glance. Pending edits flip to amber.
const EDIT_CELL_BG = "#e3f2fd5c";
const EDIT_CELL_HOVER_BG = "#e3f2fd66";
const EDIT_CELL_BORDER = "#e3f2fdff";
const EDIT_PENDING_BG = "#fff7e6";
const EDIT_PENDING_HOVER_BG = "#fff1d4";

// Maps a field's `editor` descriptor to the matching Rff* input. Same
// editor vocabulary as DetailRow so existing field configs drop straight in.
const PanelRowEditor = ({ name, editor }) => {
  switch (editor?.type) {
    case "ssn":
      return <RffSsnField name={name} suppressGrid label={null} />;
    case "phone":
      return <RffPhoneField name={name} suppressGrid label={null} />;
    case "dollar":
      return <RffDollarField name={name} suppressGrid label={null} />;
    case "number":
      return <RffNumberField name={name} suppressGrid label={null} />;
    case "count":
      // Whole-number input with the Edit Member ownership-% up/down arrows.
      return (
        <RffCountStepperField
          name={name}
          suppressGrid
          label={null}
          min={editor.min}
          suffix={editor.suffix}
        />
      );
    case "percent":
      return <RffPercentField name={name} suppressGrid label={null} />;
    case "date":
      return <RffDateField name={name} suppressGrid label={null} />;
    case "boolean":
      return <RffBooleanCheckbox name={name} suppressGrid row label={null} />;
    case "select":
      return (
        <RffSelectField
          name={name}
          suppressGrid
          label={null}
          options={editor.options || []}
        />
      );
    case "selectMultiple":
      return (
        <RffSelectMultipleCheckboxField
          name={name}
          suppressGrid
          label={null}
          options={editor.options || []}
          placeholder={editor.placeholder}
          disableStacking
          chipMaxWidth="unset"
        />
      );
    case "text":
    default:
      return <RffTextField name={name} suppressGrid label={null} />;
  }
};

// Shared row frame — fixed-width label column on the left, value/input
// column on the right (whose content is left-aligned within the column).
// Hairline divider beneath (suppressed on the last row).
const RowFrame = ({ isLast, children, sx }) => (
  <Box
    className="panel-row-frame"
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      // Tighter than the token's 10px — these rows carry 11px type now, so the
      // original padding left them looking airy relative to the text.
      py: "6px",
      minHeight: 38,
      borderBottom: isLast ? "none" : `0.5px solid ${T.color.borderDefault}`,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const RowLabel = ({ children }) => (
  <Txt
    sx={{
      fontSize: ROW_FONT,
      color: T.color.textSecondary,
      // Wraps rather than truncating — a clipped label ("Income-Producing
      // Properties Exp…") tells the reader less than a two-line one.
      whiteSpace: "normal",
      lineHeight: 1.35,
      overflowWrap: "anywhere",
    }}
  >
    {children}
  </Txt>
);

// Small "?"-style info icon shown after a label; tooltip carries the field's
// description (e.g. the model schema's field description).
const InfoTip = ({ text }) => (
  <Tooltip
    title={
      <Box sx={{ whiteSpace: "pre-line", fontSize: 11.5, lineHeight: 1.5 }}>
        {text}
      </Box>
    }
    arrow
    placement="top"
    // Lift above the drilldown sidebar/detail panes (z-index 13000) — the
    // default tooltip z-index (1500) renders behind them, so the popup was
    // invisible (only the help-cursor showed).
    slotProps={{ popper: { sx: { zIndex: 13500 } } }}
  >
    <InfoIcon
      sx={{
        fontSize: 13,
        color: T.color.textTertiary,
        cursor: "help",
        flexShrink: 0,
      }}
    />
  </Tooltip>
);

// Label column wrapper — sized to ~40% of the row so labels and values line
// up across rows. Placed as a direct flex child of RowFrame in both
// PanelRowEditable and PanelRowStatic.
const LABEL_CELL_SX = {
  flex: "0 0 40%",
  maxWidth: "40%",
  minWidth: 0,
};

const ModifiedPill = () => (
  <Txt
    component="span"
    sx={{
      ml: 0.75,
      fontSize: 9.5,
      fontWeight: T.weight.regular,
      color: T.color.infoText,
      background: T.color.infoBg,
      px: 0.6,
      py: 0.1,
      borderRadius: T.radius.sm,
      letterSpacing: "0.03em",
      textTransform: "uppercase",
    }}
  >
    Edited
  </Txt>
);

/* ── Editable row — always-on inline RFF input ──────────────────────── */
const PanelRowEditable = ({
  label,
  name,
  editor,
  computed,
  computedTooltip,
  infoTooltip,
  searchTerms,
  quickFilter,
  isLast,
}) => {
  const { meta } = useField(name, { subscription: { dirty: true } });
  const isPending = meta.dirty;

  // Multi-selects can hold several full-width chips — give them the whole
  // row width by stacking the label above the input instead of the default
  // label-left / value-right layout.
  const isStacked = editor?.type === "selectMultiple";
  // const isStacked =
  //   editor?.type === "selectMultiple" && isArray(value) && size(value) > 1;

  const visible = useMemo(
    () => fieldPassesSearch({ label, searchTerms }, quickFilter),
    [label, searchTerms, quickFilter],
  );
  if (!visible) return null;

  return (
    <RowFrame
      isLast={isLast}
      sx={
        isStacked
          ? { flexDirection: "column", alignItems: "stretch", gap: 0.75 }
          : undefined
      }
    >
      <Flx ac g={0.75} sx={{ ...LABEL_CELL_SX, overflow: "hidden" }}>
        <RowLabel>{label}</RowLabel>
        {infoTooltip && <InfoTip text={infoTooltip} />}
        {isPending && <ModifiedPill />}
      </Flx>
      <Flx
        ac
        g={0.5}
        sx={{
          flex: 1,
          minWidth: 0,
          justifyContent: "flex-start",
          // Neutralize the Rff* field's own MUI outline / wrapper chrome so
          // the input reads as part of the row, not a boxed form control.
          ".rff-input-row, .input-row-root, .input-wrapper-root": {
            border: "none !important",
            paddingBottom: "0 !important",
          },
          ".MuiOutlinedInput-notchedOutline, fieldset": {
            border: "none !important",
          },
          ".MuiInputBase-input, .MuiSelect-select, .MuiAutocomplete-input": {
            fontSize: `${ROW_FONT}px !important`,
            textAlign: "left",
            paddingLeft: `${CONTROL_INSET} !important`,
            paddingRight: "0 !important",
          },
          // Checkboxes and their labels aren't MuiInputBase — inset them to
          // the same left edge so every control in the column lines up.
          ".MuiFormControlLabel-root, .MuiCheckbox-root": {
            marginLeft: 0,
            paddingLeft: CONTROL_INSET,
          },
        }}
      >
        {computed && (
          <Tooltip
            title={
              <Box
                sx={{
                  whiteSpace: "pre-line",
                  fontFamily: FONT_MONO,
                  fontSize: 11.5,
                  lineHeight: 1.5,
                }}
              >
                {computedTooltip || "Auto-calculated value"}
              </Box>
            }
            arrow
            placement="left"
          >
            <SigmaIcon
              sx={{ fontSize: 13, color: T.color.infoText, cursor: "help" }}
            />
          </Tooltip>
        )}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            width: "100%",
            borderRadius: T.radius.md,
            // No inset here — CONTROL_INSET handles it on the control itself.
            px: 0,
            border: `1px solid ${EDIT_CELL_BORDER}`,
            background: isPending ? EDIT_PENDING_BG : EDIT_CELL_BG,
            transition: "background 0.12s",
            "&:hover": {
              background: isPending
                ? EDIT_PENDING_HOVER_BG
                : EDIT_CELL_HOVER_BG,
            },
          }}
        >
          <PanelRowEditor name={name} editor={editor} />
        </Box>
      </Flx>
    </RowFrame>
  );
};

/* ── Static row — filled / blocked / locked / empty ─────────────────── */
const PanelRowStatic = ({
  label,
  value,
  state,
  blockedReason,
  computed,
  computedTooltip,
  infoTooltip,
  editable,
  onAdd,
  onValueClick,
  valueClickHint = "Open where this can be edited",
  searchTerms,
  quickFilter,
  isLast,
}) => {
  const hasValue = !isNil(value) && value !== "" && value !== "—";

  // Resolve the presentation state. Callers can force it via `state`;
  // otherwise it's derived from the field's flags + whether a value exists.
  const resolved =
    state ||
    (computed
      ? hasValue
        ? "filled"
        : "blocked"
      : editable
        ? hasValue
          ? "filled"
          : onAdd
            ? "empty"
            : "filled"
        : "locked");

  const visible = useMemo(
    () =>
      fieldPassesSearch(
        { label, value: hasValue ? value : undefined, searchTerms },
        quickFilter,
      ),
    [label, value, hasValue, searchTerms, quickFilter],
  );
  if (!visible) return null;

  return (
    <RowFrame isLast={isLast}>
      <Flx ac g={0.5} sx={{ ...LABEL_CELL_SX, overflow: "hidden" }}>
        <RowLabel>{label}</RowLabel>
        {infoTooltip && <InfoTip text={infoTooltip} />}
      </Flx>

      {resolved === "filled" && (
        <Flx
          ac
          g={0.5}
          sx={{ flex: 1, minWidth: 0, justifyContent: "flex-end" }}
        >
          {computed && (
            <Tooltip
              title={
                <Box
                  sx={{
                    whiteSpace: "pre-line",
                    fontFamily: FONT_MONO,
                    fontSize: 11.5,
                    lineHeight: 1.5,
                  }}
                >
                  {computedTooltip || "Auto-calculated value"}
                </Box>
              }
              arrow
              placement="left"
            >
              <SigmaIcon
                sx={{ fontSize: 13, color: T.color.infoText, cursor: "help" }}
              />
            </Tooltip>
          )}
          <Txt
            sx={{
              fontSize: ROW_FONT,
              fontWeight: T.weight.medium,
              color: hasValue ? T.color.textPrimary : T.color.textTertiary,
              fontStyle: hasValue ? "normal" : "italic",
              textAlign: "right",
              wordBreak: "break-word",
            }}
          >
            {hasValue ? value : "Not set"}
          </Txt>
        </Flx>
      )}

      {resolved === "blocked" && (
        <Flx
          ac
          g={0.5}
          sx={{ flex: 1, minWidth: 0, justifyContent: "flex-end" }}
        >
          {computed && (
            <Tooltip
              title={
                <Box
                  sx={{
                    whiteSpace: "pre-line",
                    fontFamily: FONT_MONO,
                    fontSize: 11.5,
                    lineHeight: 1.5,
                  }}
                >
                  {computedTooltip || "Auto-calculated value"}
                </Box>
              }
              arrow
              placement="left"
            >
              <SigmaIcon
                sx={{ fontSize: 13, color: T.color.infoText, cursor: "help" }}
              />
            </Tooltip>
          )}
          <Txt
            sx={{
              fontSize: ROW_FONT,
              color: T.color.textTertiary,
              fontStyle: "italic",
              textAlign: "right",
            }}
          >
            {blockedReason || "Not yet available"}
          </Txt>
        </Flx>
      )}

      {resolved === "locked" && (
        <Flx
          ac
          g={0.75}
          sx={{ flex: 1, minWidth: 0, justifyContent: "flex-end" }}
        >
          <Txt
            onClick={onValueClick}
            sx={{
              fontSize: ROW_FONT,
              color: hasValue ? T.color.textPrimary : T.color.textTertiary,
              fontWeight: hasValue ? T.weight.medium : T.weight.regular,
              fontStyle: hasValue ? "normal" : "italic",
              textAlign: "right",
              wordBreak: "break-word",
              // A locked row can still point somewhere it CAN be edited.
              ...(onValueClick && {
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }),
            }}
          >
            {hasValue ? value : "Not set"}
          </Txt>
          <Tooltip
            title={onValueClick ? valueClickHint : "This field can't be edited here"}
            arrow
            placement="left"
            slotProps={{ popper: { sx: { zIndex: 13500 } } }}
          >
            <LockIcon
              sx={{ fontSize: 12, color: T.color.textTertiary, cursor: "help" }}
            />
          </Tooltip>
        </Flx>
      )}

      {resolved === "empty" && (
        <Box
          component="button"
          type="button"
          onClick={onAdd}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            fontSize: T.text.sm,
            fontFamily: "inherit",
            color: T.color.textSecondary,
            px: "10px",
            py: "4px",
            border: `0.5px dashed ${T.color.borderStrong}`,
            borderRadius: T.radius.md,
            background: "transparent",
            cursor: "pointer",
            "&:hover": { background: T.color.bgSubtle },
          }}
        >
          <AddIcon sx={{ fontSize: 12 }} />
          Add {String(label || "").toLowerCase()}
        </Box>
      )}
    </RowFrame>
  );
};

const PanelRow = (props) => {
  const renderInline = Boolean(
    props.editable && props.name && props.editor && props.state !== "empty",
  );
  return renderInline ? (
    <PanelRowEditable {...props} />
  ) : (
    <PanelRowStatic {...props} />
  );
};

export default PanelRow;
